import { AxiosInstance } from "axios";
import { useDispatch } from "react-redux";
import { setProfileUser } from "../../store/user/actions";
import { AuthLoginResponseService } from "../auth/AuthenticationServices.type";
import {
  BillingPolicyHistoryLatest,
  UserProfileResponseService,
  Company,
} from "./UserTypes.type";
import { OnEditSubmitTypes, OnSubmitTypes } from "../../pages/others/userModule/onSubmitTypes.types";

const resolvePortalTermsAccepted = (
  billingPolicyAproved: boolean | undefined,
): boolean => {
  // If missing, default to true to avoid blocking existing tenants.
  if (billingPolicyAproved !== undefined) return billingPolicyAproved;
  return true;
};

type BillingPolicyHistoryResponse = BillingPolicyHistoryLatest & {
  id?: string;
  updatedDate?: string;
  deletedAt?: string | null;
};

const getLatestBillingPolicyHistory = (
  history: BillingPolicyHistoryResponse[] | undefined,
): BillingPolicyHistoryLatest | undefined => {
  if (!history || history.length === 0) return undefined;
  return history.reduce<BillingPolicyHistoryResponse | undefined>((best, cur) => {
    const bestTime = best?.createdDate ? new Date(best.createdDate).getTime() : -Infinity;
    const curTime = cur?.createdDate ? new Date(cur.createdDate).getTime() : -Infinity;
    if (!best) return cur;
    return curTime > bestTime ? cur : best;
  }, undefined);
};

export default class UserService {
  private axios: AxiosInstance;
  public dispatch = useDispatch();
  constructor(axios: AxiosInstance) {
    this.axios = axios;
  }

  private rolesAllowed = ["RRHH", "RRHH_APPOINTMENTS_AND_ABSENTEE", "RRHH_RESULT", "RRHH_APPOINTMENTS", "CANDIDATE_APPOINTMENTS", "RRHH_ABSENTEE", "COMPANY_ADMIN", "COMPANY_ACCOUNTANT", "PARTICULAR_CLIENT", "ADM_ABSENTEE", "ASESOR"]

  public async getProfile(tokens: AuthLoginResponseService, currentCompany = null): Promise<any> {
    try {
      const url: string = "/users/profile";
      const response: UserProfileResponseService = await this.axios.get(url, {
        headers: { Authorization: `Bearer ${tokens.access_token}` },
      });
      let formattedResponse = response;

      let chosenRole: string[] = response.roles;
      if (chosenRole.some(role => this.rolesAllowed.includes(role))) {
        let HeadOffice: Company = response.companies?.find(x => x?.headOfficeId === null);
        formattedResponse["currentCompany"] = !currentCompany ? HeadOffice : currentCompany //Make Matrix Branch the currentCompany if currentCompany is null
        response.roles && (formattedResponse["currentRole"] = this.chooseCurrentRole(chosenRole));
        formattedResponse["userId"] = response.userId // Choose a Current Role.
        formattedResponse["portalTermsAccepted"] = resolvePortalTermsAccepted(
          response.billing_policy_aproved,
        );

        // Billing conditions data (latest policy history) is fetched at login.
        const companyId = formattedResponse["currentCompany"]?.id ?? response.companies?.[0]?.id;
        if (companyId) {
          try {
            const historyUrl = `/company/billing-policy/${companyId}/history`;
            const history: BillingPolicyHistoryResponse[] = await this.axios.get(historyUrl);
            formattedResponse["billingPolicyLatest"] = getLatestBillingPolicyHistory(history);
          } catch (e) {
            // Non-blocking: allow login even if billing policy history fails.
            formattedResponse["billingPolicyLatest"] = undefined;
          }
        }

        this.dispatch(setProfileUser(formattedResponse));
      } else {
        return 'user not authorized to login in'
      }
    } catch (error) {
      console.log(error);
    }
  }
  public async findUsersByRoleAlias(alias: string): Promise<any> {
    const url: string = `/users/roleInCompany/${alias}`;
    const response = await this.axios.get(url);
    return response;
  }

  public async findUserByUsername(username: string): Promise<any> {
    const url: string = `/users?username=${username}`;
    const response = await this.axios.get(url);
    return response;
  }

  public async createUsersByRoleAlias(body: OnSubmitTypes): Promise<any> {
    const url: string = `/users/register`;
    const response = await this.axios.post(url, body);
    return response;
  }

  public async getIndividualProfile(
    id: string,
  ): Promise<void> {
    const url: string = `/users/profile/${id}`;
    const response: UserProfileResponseService = await this.axios.get(url, {});
    response.roles && (response["currentRole"] = this.chooseCurrentRole(response.roles));
    response["portalTermsAccepted"] = resolvePortalTermsAccepted(
      response.billing_policy_aproved,
    );
    this.dispatch(setProfileUser(response));
  }

  public async updateUserById(user: any, id: string): Promise<any> {
    const url: string = `/users/${id}`;
    const response = await this.axios.put(url, user);
    return response;
  }

  public async updateUserRol(id: string, rol: string,): Promise<any> {
    const url: string = `/users/rol/${id}`;
    const response = await this.axios.put(url, { rol });
    return response;
  }

  public async findUserById(id: string): Promise<any> {
    const url: string = `/users/profile/${id}`;
    const response = await this.axios.get(url);
    return response;
  }

  public async updateUserLoggedInPassword(): Promise<string> {
    const url: string = `/users/updateUserLoggedInPassword`;
    const response: string = await this.axios.post(url);
    return response;
  }

  //Define How Current Role Gets Set in Redux
  public chooseCurrentRole(roles: any) {
    const isParticulaClient = roles?.includes("PARTICULAR_CLIENT");
    const isCompanyAdmin = roles?.includes("COMPANY_ADMIN");
    const isCompanyAccountant = roles?.includes("COMPANY_ACCOUNTANT");
    const isRRHH = roles?.includes("RRHH");
    const isRRHHTurnSRes = roles?.includes("RRHH_APPOINTMENTS_AND_ABSENTEE");
    const isRRHHResult = roles?.includes("RRHH_RESULT");
    const isRRHHAppointments = roles?.includes("RRHH_APPOINTMENTS");
    const isRRAbsentee = roles?.includes("RRHH_ABSENTEE");
    const isADMabsentee = roles?.includes("ADM_ABSENTEE");
    const isAsesor = roles?.includes("ASESOR");


    if (isParticulaClient) {
      return "PARTICULAR_CLIENT"
    } else if (isCompanyAdmin) {
      return "COMPANY_ADMIN"
    } else if (isCompanyAccountant) {
      return "COMPANY_ACCOUNTANT"
    } else if (isRRHH) {
      return "RRHH"
    } else if (isRRHHTurnSRes) {
      return "RRHH_APPOINTMENTS_AND_ABSENTEE"
    } else if (isRRHHResult) {
      return "RRHH_RESULT"
    } else if (isRRHHAppointments) {
      return "RRHH_APPOINTMENTS"
    } else if (isRRAbsentee) {
      return "RRHH_ABSENTEE"
    } else if (isADMabsentee) {
      return 'ADM_ABSENTEE'
    } else if (isAsesor) {
      return 'ASESOR'
    } else {
      return roles[0]
    }


    // const chosenRole: string = isParticulaClient
    //   ? "PARTICULAR_CLIENT"
    //   : roles
    //     ? roles[0]
    //     : "";

    // return chosenRole;
  }
}
